import { useEffect, useRef } from 'react';

const focusableSelector =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

interface DialogEntry {
  id: symbol;
  element: HTMLElement;
  close: () => void;
  initialFocus: HTMLElement | null;
  restoreTarget: HTMLElement | null;
  focusFrame: number;
}

const dialogStack: DialogEntry[] = [];
let previousBodyOverflow: string | null = null;

function isVisible(element: HTMLElement): boolean {
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  if (element.matches('input[type="hidden"]')) return false;
  const visibility = window.getComputedStyle(element).visibility;
  if (visibility === 'hidden' || visibility === 'collapse') return false;
  for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
    if (window.getComputedStyle(ancestor).display === 'none') return false;
  }
  return true;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector))
    .filter((element) => element.tabIndex >= 0 && isVisible(element))
    .sort((first, second) => {
      const position = first.compareDocumentPosition(second);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
}

function focusEntry(entry: DialogEntry): void {
  const preferred =
    entry.initialFocus ?? entry.element.querySelector<HTMLElement>('[data-autofocus]');
  const focusable =
    preferred && isVisible(preferred) && !preferred.matches(':disabled')
      ? preferred
      : getFocusableElements(entry.element)[0];
  (focusable ?? entry.element).focus();
}

function handleDialogKeyDown(event: KeyboardEvent): void {
  const entry = dialogStack[dialogStack.length - 1];
  if (!entry) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopImmediatePropagation();
    entry.close();
    return;
  }

  if (event.key !== 'Tab') return;
  const focusable = getFocusableElements(entry.element);
  if (focusable.length === 0) {
    event.preventDefault();
    entry.element.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const outside = !entry.element.contains(document.activeElement);
  if (event.shiftKey && (document.activeElement === first || outside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || outside)) {
    event.preventDefault();
    first.focus();
  }
}

function registerDialog(entry: DialogEntry): () => void {
  if (dialogStack.length === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.addEventListener('keydown', handleDialogKeyDown, true);
  }
  document.body.style.overflow = 'hidden';
  dialogStack.push(entry);
  entry.focusFrame = requestAnimationFrame(() => {
    // A user or an autofocus field may already have moved focus into this
    // dialog before the frame runs. Do not replace that deliberate focus.
    if (
      dialogStack[dialogStack.length - 1] === entry &&
      !entry.element.contains(document.activeElement)
    ) {
      focusEntry(entry);
    }
  });

  return () => {
    cancelAnimationFrame(entry.focusFrame);
    const index = dialogStack.findIndex(({ id }) => id === entry.id);
    if (index < 0) return;

    const wasTopmost = index === dialogStack.length - 1;
    dialogStack.splice(index, 1);
    const nextTop = dialogStack[dialogStack.length - 1];

    if (nextTop) {
      // If an underlying dialog unmounts while another opens, pass its page
      // return target forward instead of retaining a detached child element.
      if (
        nextTop.restoreTarget &&
        (entry.element.contains(nextTop.restoreTarget) || !nextTop.restoreTarget.isConnected)
      ) {
        nextTop.restoreTarget = entry.restoreTarget;
      }
      if (wasTopmost) {
        if (entry.restoreTarget?.isConnected && nextTop.element.contains(entry.restoreTarget)) {
          entry.restoreTarget.focus();
        } else {
          focusEntry(nextTop);
        }
      }
      return;
    }

    document.removeEventListener('keydown', handleDialogKeyDown, true);
    document.body.style.overflow = previousBodyOverflow ?? '';
    previousBodyOverflow = null;
    if (entry.restoreTarget?.isConnected) entry.restoreTarget.focus();
  };
}

interface UseDialogFocusOptions {
  isOpen: boolean;
  onClose: () => void;
  initialFocusRef?: { readonly current: HTMLElement | null };
}

/** Shared focus, Escape, restoration, and body-lock behavior for every dialog/drawer. */
export function useDialogFocus<T extends HTMLElement = HTMLDivElement>({
  isOpen,
  onClose,
  initialFocusRef,
}: UseDialogFocusOptions) {
  const dialogRef = useRef<T>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const idRef = useRef(Symbol('dialog'));

  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;
    const entry: DialogEntry = {
      id: idRef.current,
      element: dialogRef.current,
      close: () => closeRef.current(),
      initialFocus: initialFocusRef?.current ?? null,
      restoreTarget: document.activeElement as HTMLElement | null,
      focusFrame: 0,
    };
    return registerDialog(entry);
  }, [initialFocusRef, isOpen]);

  return dialogRef;
}
